import React, { useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MdAdd, MdEdit, MdDelete, MdExpandMore, MdExpandLess, MdDownload, MdDragIndicator } from 'react-icons/md';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import { Baseurl } from '../../../../Api/Baseurl';
import { toast } from 'react-toastify';
import { ToastOptions } from '../../../../helpers/ToastOptions';
import Swal from 'sweetalert2';
import { useQuery } from 'react-query';
import './CourseResources.scss';
import { useRoleBasedRoute } from '../../../../utils/routeUtils';
import { DraggableItem, DropTarget, useReorder, useDraggableItem, useDnD, ENTITY_TYPES, SCOPE_TYPES } from '../../../../components/DnD';

const CourseResources = ({ activeTab: propActiveTab }) => {
    const { courseId } = useParams();
    const { getRoute } = useRoleBasedRoute();
    const token = localStorage.getItem('medtoken');
    const [activeTab, setActiveTab] = useState(propActiveTab || 'qbanks');
    const [expandedSections, setExpandedSections] = useState({});
    const [expandedChapters, setExpandedChapters] = useState({});
    const [selectedChapterId, setSelectedChapterId] = useState(null);
    
   
    const expandedSectionsRef = useRef({});
    const expandedChaptersRef = useRef({});
    

    expandedSectionsRef.current = expandedSections;
    expandedChaptersRef.current = expandedChapters;

    
    React.useEffect(() => {
        if (propActiveTab) {
            setActiveTab(propActiveTab);
        }
    }, [propActiveTab]);

    // Form states
    const [showAddQbank, setShowAddQbank] = useState(false);
    const [showAddFile, setShowAddFile] = useState(false);
    const [showAddFlashcard, setShowAddFlashcard] = useState(false);
    const [showEditQbank, setShowEditQbank] = useState(false);
    const [showEditFile, setShowEditFile] = useState(false);
    const [showEditFlashcard, setShowEditFlashcard] = useState(false);

    // Edit form states
    const [editQbankForm, setEditQbankForm] = useState({
        id: '',
        title: '',
        description: '',
        file: null
    });

    const [editFileForm, setEditFileForm] = useState({
        id: '',
        title: '',
        file: null,
        isDownload: true
    });

    const [editFlashcardForm, setEditFlashcardForm] = useState({
        id: '',
        title: '',
        description: '',
        cards: []
    });

    // Fetch resource structures based on active tab
    const fetchQBankStructure = async () => {
        const response = await axios.get(`${Baseurl}/admin/getQBankStructure/${courseId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data.payload || {};
    };

    const fetchFileStructure = async () => {
        const response = await axios.get(`${Baseurl}/admin/getFileStructure/${courseId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data.payload || {};
    };

    const fetchFlashcardStructure = async () => {
        const response = await axios.get(`${Baseurl}/admin/getFlashcardStructure/${courseId}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data.payload || {};
    };

    // Use React Query for resource structures
    const { data: qbankStructure = {}, isLoading: isQbankLoading, refetch: refetchQbanks } = useQuery(
        ["qbankStructure", courseId],
        fetchQBankStructure,
        { 
            enabled: activeTab === 'qbanks',
            staleTime: 0,
            cacheTime: 0, 
            refetchOnWindowFocus: false
        }
    );

    const { data: fileStructure = {}, isLoading: isFileLoading, refetch: refetchFiles } = useQuery(
        ["fileStructure", courseId],
        fetchFileStructure,
        { 
            enabled: activeTab === 'files',
            staleTime: 0, 
            cacheTime: 0, 
            refetchOnWindowFocus: false
        }
    );

    const { data: flashcardStructure = {}, isLoading: isFlashcardLoading, refetch: refetchFlashcards } = useQuery(
        ["flashcardStructure", courseId],
        fetchFlashcardStructure,
        { 
            enabled: activeTab === 'flashcards',
            staleTime: 0, 
            cacheTime: 0, 
            refetchOnWindowFocus: false
        }
    );

   
    const getActiveData = () => {
        switch (activeTab) {
            case 'qbanks':
                return qbankStructure;
            case 'files':
                return fileStructure;
            case 'flashcards':
                return flashcardStructure;
            default:
                return {};
        }
    };

    const activeData = getActiveData();

    // Drag and Drop hooks
    const qbankReorder = useReorder(ENTITY_TYPES.QBANK, SCOPE_TYPES.CHAPTER, courseId);
    const fileReorder = useReorder(ENTITY_TYPES.FILE, SCOPE_TYPES.CHAPTER, courseId);
    const flashcardReorder = useReorder(ENTITY_TYPES.FLASHCARD, SCOPE_TYPES.CHAPTER, courseId);
    const { handleTransfer, handleMoveToEnd, handleMoveToBeginning } = useDnD();

    // Unified refetch function
    const refetchAllData = async () => {
        try {
            await Promise.all([
                refetchQbanks(),
                refetchFiles(),
                refetchFlashcards()
            ]);
        } catch (error) {
            console.error('Error refetching data:', error);
        }
    };

    // Toggle section expansion
    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    // Toggle chapter expansion
    const toggleChapter = (chapterId) => {
        setExpandedChapters(prev => ({
            ...prev,
            [chapterId]: !prev[chapterId]
        }));
    };

    
    React.useEffect(() => {
        if (activeData.sections) {
            const expanded = {};
            activeData.sections.forEach(section => {
               
                expanded[section.id] = expandedSectionsRef.current[section.id] !== undefined ? expandedSectionsRef.current[section.id] : false;
            });
            setExpandedSections(expanded);
        }
    }, [activeData.sections]);

   
    React.useEffect(() => {
        if (activeData.sections) {
            const expanded = {};
            activeData.sections.forEach(section => {
                if (section.chapters) {
                    section.chapters.forEach(chapter => {
                     
                        expanded[chapter.id] = expandedChaptersRef.current[chapter.id] !== undefined ? expandedChaptersRef.current[chapter.id] : false;
                    });
                }
            });
            setExpandedChapters(expanded);
        }
    }, [activeData.sections]);

    // Qbank Formik
    const qbankValidation = Yup.object({
        title: Yup.string().required('Title is required'),
        description: Yup.string(),
        type: Yup.string().oneOf(['Graded Quiz', 'Practice Quiz'], 'Invalid quiz type').required('Type is required')
    });

    const qbankFormik = useFormik({
        initialValues: {
            title: '',
            description: '',
            type: 'Practice Quiz'
        },
        validationSchema: qbankValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await axios.post(`${Baseurl}/admin/qbanks`, {
                    title: values.title,
                    description: values.description,
                    type: values.type,
                    chapterId: selectedChapterId,
                    courseId: courseId
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.msg === 'QBank created successfully') {
                    toast.success('Qbank added successfully', ToastOptions('success'));
                    setShowAddQbank(false);
                    resetForm();
                    refetchQbanks();
                }
            } catch (error) {
                toast.error(error.response?.data?.msg || 'Failed to add Qbank', ToastOptions('error'));
            }
        }
    });

    // Edit Qbank Formik
    const editQbankFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: editQbankForm.title || '',
            description: editQbankForm.description || '',
            type: editQbankForm.type || 'Practice Quiz'
        },
        validationSchema: qbankValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await axios.put(`${Baseurl}/admin/qbanks/${editQbankForm.id}`, {
                    title: values.title,
                    description: values.description,
                    type: values.type
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.msg === 'QBank updated successfully') {
                    toast.success('Qbank updated successfully', ToastOptions('success'));
                    setShowEditQbank(false);
                    setEditQbankForm({ id: '', title: '', description: '', file: null });
                    resetForm();
                    refetchQbanks();
                }
            } catch (error) {
                toast.error(error.response?.data?.msg || 'Failed to update Qbank', ToastOptions('error'));
            }
        }
    });

    // File Formik
    const fileValidation = Yup.object({
        title: Yup.string().required('Title is required'),
        file: Yup.mixed().required('File is required'),
        isDownload: Yup.boolean().required('Download option is required')
    });

    // Edit File Validation
    const editFileValidation = Yup.object({
        title: Yup.string().required('Title is required'),
        file: Yup.mixed().nullable(),
        isDownload: Yup.boolean().required('Download option is required')
    });

    const fileFormik = useFormik({
        initialValues: {
            title: '',
            file: null,
            isDownload: true
        },
        validationSchema: fileValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const formData = new FormData();
                formData.append('name', values.title);
                formData.append('file', values.file);
                formData.append('isDownload', values.isDownload);
                formData.append('chapterId', selectedChapterId);
                formData.append('courseId', courseId);

                const response = await axios.post(`${Baseurl}/admin/files`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });

               
                if (response.data.msg === 'File uploaded successfully') {
                    toast.success('File added successfully', ToastOptions('success'));
                    setShowAddFile(false);
                    resetForm();
                    refetchFiles();
                } else {
                    toast.error('Failed to add file', ToastOptions('error'));
                }
            } catch (error) {
                console.error('Error adding file:', error);
                toast.error(error.response?.data?.msg || 'Failed to add file', ToastOptions('error'));
            }
        }
    });

    // Flashcard Formik
    const flashcardValidation = Yup.object({
        title: Yup.string().required('Title is required')
    });

    const flashcardFormik = useFormik({
        initialValues: {
            title: ''
        },
        validationSchema: flashcardValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await axios.post(`${Baseurl}/admin/flashcards`, {
                    title: values.title,
                    chapterId: selectedChapterId,
                    courseId: courseId
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.msg === 'Flashcard set created successfully') {
                    toast.success('Flashcard set added successfully', ToastOptions('success'));
                    setShowAddFlashcard(false);
                    resetForm();
                    refetchFlashcards();
                } else {
                    toast.error('Failed to add flashcard set', ToastOptions('error'));
                }
            } catch (error) {
                console.error('Error adding flashcard set:', error);
                toast.error(error.response?.data?.msg || 'Failed to add flashcard set', ToastOptions('error'));
            }
        }
    });

    // Edit Flashcard Formik
    const editFlashcardFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: editFlashcardForm.title || ''
        },
        validationSchema: flashcardValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await axios.put(`${Baseurl}/admin/flashcards/${editFlashcardForm.id}`, {
                    title: values.title
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.msg === 'Flashcard set updated successfully') {
                    toast.success('Flashcard set updated successfully', ToastOptions('success'));
                    setShowEditFlashcard(false);
                    setEditFlashcardForm({ id: '', title: '' });
                    resetForm();
                    refetchFlashcards();
                } else {
                    toast.error('Failed to update flashcard set', ToastOptions('error'));
                }
            } catch (error) {
                console.error('Error updating flashcard set:', error);
                toast.error(error.response?.data?.msg || 'Failed to update flashcard set', ToastOptions('error'));
            }
        }
    });

    // Delete handlers
    const handleDeleteQbank = async (qbankId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This Qbank will be permanently deleted",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#01677e",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${Baseurl}/admin/qbanks/${qbankId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Qbank deleted successfully', ToastOptions('success'));
                    refetchQbanks();
                } catch (error) {
                    toast.error('Failed to delete Qbank', ToastOptions('error'));
                }
            }
        });
    };

    const handleDeleteFile = async (fileId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This file will be permanently deleted",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#01677e",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${Baseurl}/admin/files/${fileId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('File deleted successfully', ToastOptions('success'));
                    refetchFiles();
                } catch (error) {
                    toast.error('Failed to delete file', ToastOptions('error'));
                }
            }
        });
    };

    const handleDeleteFlashcard = async (flashcardId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This flashcard set will be permanently deleted",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#01677e",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        }).then(async (res) => {
            if (res.isConfirmed) {
                try {
                    await axios.delete(`${Baseurl}/admin/flashcards/${flashcardId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Flashcard set deleted successfully', ToastOptions('success'));
                    refetchFlashcards();
                } catch (error) {
                    toast.error('Failed to delete flashcard set', ToastOptions('error'));
                }
            }
        });
    };

    // Update the file edit form
    const editFileFormik = useFormik({
        enableReinitialize: true,
        initialValues: {
            title: editFileForm.title || '',
            file: null,
            isDownload: editFileForm.isDownload !== undefined ? editFileForm.isDownload : true
        },
        validationSchema: editFileValidation,
        onSubmit: async (values, { resetForm }) => {
            try {
                const formData = new FormData();
                formData.append('name', values.title);
                formData.append('isDownload', values.isDownload);
                if (values.file) {
                    formData.append('file', values.file);
                }

                const response = await axios.put(
                    `${Baseurl}/admin/files/${editFileForm.id}`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (response.data.msg === 'File updated successfully') {
                    toast.success('File updated successfully', ToastOptions('success'));
                    setShowEditFile(false);
                    setEditFileForm({ id: '', title: '', file: null, isDownload: true });
                    resetForm();
                    refetchFiles();
                } else {
                    toast.error('Failed to update file', ToastOptions('error'));
                }
            } catch (error) {
                console.error('Error updating file:', error);
                toast.error(error.response?.data?.msg || 'Failed to update file', ToastOptions('error'));
            }
        }
    });

    // Drag and Drop Components
    const QbankDragItem = ({ qbank, chapterId, index, qbanksArray }) => {
        const { drag } = useDraggableItem('qbank', qbank, index);
        
        const handleReorder = async (draggedItem, targetIndex, itemsArray) => {
            if (draggedItem.index !== targetIndex) {
                await qbankReorder.reorder(draggedItem, targetIndex, SCOPE_TYPES.CHAPTER, chapterId, itemsArray);
                await refetchQbanks();
                await refetchAllData();
            }
        };

        const handleMoveToTop = async () => {
            try {
                await handleMoveToBeginning(ENTITY_TYPES.QBANK, qbank.id, SCOPE_TYPES.CHAPTER, chapterId);
                await refetchQbanks();
                await refetchAllData();
            } catch (error) {
                console.error('Error moving qbank to top:', error);
                toast.error('Failed to move qbank to top', ToastOptions('error'));
            }
        };

        const handleMoveToBottom = async () => {
            try {
                await handleMoveToEnd(ENTITY_TYPES.QBANK, qbank.id, SCOPE_TYPES.CHAPTER, chapterId);
                await refetchQbanks();
                await refetchAllData();
            } catch (error) {
                console.error('Error moving qbank to bottom:', error);
                toast.error('Failed to move qbank to bottom', ToastOptions('error'));
            }
        };

        return (
            <DraggableItem
                type="qbank"
                item={qbank}
                index={index}
                className="resource-item"
                showDragHandle={false}
                onDragEnd={(draggedItem, monitor) => {
                    if (monitor.didDrop()) {
                        const dropResult = monitor.getDropResult();
                        if (dropResult && dropResult.index !== undefined) {
                            handleReorder(draggedItem, dropResult.index, qbanksArray);
                        }
                    }
                }}
            >
                                                        <div className="resource-header">
                    <div className="resource-title">
                                                                <div className="title-section">
                                                                    <h5>{qbank.title}</h5>
                                                                    <span className={`type-badge ${qbank.type === 'Graded Quiz' ? 'graded' : 'practice'}`}>
                                                                        {qbank.type}
                                                                    </span>
                                                                </div>
                                                                {qbank.description && (
                                                                    <p className="description">{qbank.description}</p>
                                                                )}
                                                                <div className="meta-info">
                                                                    <span className="meta-item">
                                                                        <i className="fas fa-calendar"></i>
                                                                        Added: {new Date(qbank.createdAt).toLocaleDateString()}
                                                                    </span>
                                                                    {qbank.updatedAt && (
                                                                        <span className="meta-item">
                                                                            <i className="fas fa-clock"></i>
                                                                            Updated: {new Date(qbank.updatedAt).toLocaleDateString()}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                    <div className="resource-actions">
                        <div 
                            ref={drag} 
                            className="drag-handle"
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            <MdDragIndicator />
                        </div>
                        <button 
                            onClick={handleMoveToTop}
                            className="move-btn move-top"
                            title="Move to Top"
                        >
                            ↑
                        </button>
                        <button 
                            onClick={handleMoveToBottom}
                            className="move-btn move-bottom"
                            title="Move to Bottom"
                        >
                            ↓
                        </button>
                                                                <Link
                                                                    to={getRoute(`Questions/${qbank.id}`)}
                                                                    className="manage-btn"
                                                                >
                                                                    Manage Questions
                                                                </Link>
                                                                <button
                                                                    className="edit-btn"
                                                                    onClick={() => {
                                                                        setEditQbankForm({
                                                                            id: qbank.id,
                                                                            title: qbank.title,
                                                                            description: qbank.description || '',
                                                                            type: qbank.type
                                                                        });
                                                                        setShowEditQbank(true);
                                                                    }}
                                                                >
                                                                    <MdEdit />
                                                                </button>
                                                                <button
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteQbank(qbank.id)}
                                                                >
                                                                    <MdDelete />
                                                                </button>
                                                            </div>
                                                        </div>
            </DraggableItem>
        );
    };

    const FileDragItem = ({ file, chapterId, index, filesArray }) => {
        const { drag } = useDraggableItem('file', file, index);
        
        const handleReorder = async (draggedItem, targetIndex, itemsArray) => {
            if (draggedItem.index !== targetIndex) {
                await fileReorder.reorder(draggedItem, targetIndex, SCOPE_TYPES.CHAPTER, chapterId, itemsArray);
                await refetchFiles();
                await refetchAllData();
            }
        };

        const handleMoveToTop = async () => {
            try {
                await handleMoveToBeginning(ENTITY_TYPES.FILE, file.id, SCOPE_TYPES.CHAPTER, chapterId);
                await refetchFiles();
                await refetchAllData();
            } catch (error) {
                console.error('Error moving file to top:', error);
                toast.error('Failed to move file to top', ToastOptions('error'));
            }
        };

        const handleMoveToBottom = async () => {
            try {
                await handleMoveToEnd(ENTITY_TYPES.FILE, file.id, SCOPE_TYPES.CHAPTER, chapterId);
                await refetchFiles();
                await refetchAllData();
            } catch (error) {
                console.error('Error moving file to bottom:', error);
                toast.error('Failed to move file to bottom', ToastOptions('error'));
            }
        };

        return (
            <DraggableItem
                type="file"
                item={file}
                index={index}
                className="resource-item"
                showDragHandle={false}
                onDragEnd={(draggedItem, monitor) => {
                    if (monitor.didDrop()) {
                        const dropResult = monitor.getDropResult();
                        if (dropResult && dropResult.index !== undefined) {
                            handleReorder(draggedItem, dropResult.index, filesArray);
                        }
                    }
                }}
            >
                                                        <div className="resource-header">
                                                            <div className="resource-title">
                                                                <h5>{file.name}</h5>
                                                                <p>Type: {file.fileType}</p>
                                                                <p>Downloadable: {file.isDownload ? 'Yes' : 'No'}</p>
                                                                <p>Added: {new Date(file.createdAt).toLocaleDateString()}</p>
                                                            </div>
                                                            <div className="resource-actions">
                        <div 
                            ref={drag} 
                            className="drag-handle"
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            <MdDragIndicator />
                        </div>
                        <button 
                            onClick={handleMoveToTop}
                            className="move-btn move-top"
                            title="Move to Top"
                        >
                            ↑
                        </button>
                        <button 
                            onClick={handleMoveToBottom}
                            className="move-btn move-bottom"
                            title="Move to Bottom"
                        >
                            ↓
                        </button>
                        <button
                            className="download-btn"
                            onClick={async () => {
                                try {
                                    const response = await fetch(`${Baseurl}/${file.path}`, {
                                        headers: {
                                            Authorization: `Bearer ${token}`
                                        }
                                    });
                                    
                                    if (response.ok) {
                                        const blob = await response.blob();
                                        const url = window.URL.createObjectURL(blob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = file.name;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        window.URL.revokeObjectURL(url);
                                    } else {
                                        toast.error('Failed to download file', ToastOptions('error'));
                                    }
                                } catch (error) {
                                    console.error('Download error:', error);
                                    toast.error('Failed to download file', ToastOptions('error'));
                                }
                            }}
                            title="Download File"
                        >
                            <MdDownload />
                        </button>
                                                                <button
                                                                    className="edit-btn"
                                                                    onClick={() => {
                                                                        setEditFileForm({
                                                                            id: file.id,
                                                                            title: file.name,
                                                                            file: null,
                                                                            isDownload: file.isDownload
                                                                        });
                                                                        setShowEditFile(true);
                                                                    }}
                                                                >
                                                                    <MdEdit />
                                                                </button>
                                                                <button
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteFile(file.id)}
                                                                >
                                                                    <MdDelete />
                                                                </button>
                                                            </div>
                                                        </div>
            </DraggableItem>
        );
    };

    const FlashcardDragItem = ({ flashcard, chapterId, index, flashcardsArray }) => {
        const { drag } = useDraggableItem('flashcard', flashcard, index);
        
        const handleReorder = async (draggedItem, targetIndex, itemsArray) => {
            if (draggedItem.index !== targetIndex) {
                await flashcardReorder.reorder(draggedItem, targetIndex, SCOPE_TYPES.CHAPTER, chapterId, itemsArray);
                await refetchFlashcards();
                await refetchAllData();
            }
        };

        const handleMoveToTop = async () => {
            try {
                await handleMoveToBeginning(ENTITY_TYPES.FLASHCARD, flashcard.id, SCOPE_TYPES.CHAPTER, chapterId);
                await refetchFlashcards();
                await refetchAllData();
            } catch (error) {
                console.error('Error moving flashcard to top:', error);
                toast.error('Failed to move flashcard to top', ToastOptions('error'));
            }
        };

        const handleMoveToBottom = async () => {
            try {
                await handleMoveToEnd(ENTITY_TYPES.FLASHCARD, flashcard.id, SCOPE_TYPES.CHAPTER, chapterId);
                await refetchFlashcards();
                await refetchAllData();
            } catch (error) {
                console.error('Error moving flashcard to bottom:', error);
                toast.error('Failed to move flashcard to bottom', ToastOptions('error'));
            }
        };

        return (
            <DraggableItem
                type="flashcard"
                item={flashcard}
                index={index}
                className="resource-item"
                showDragHandle={false}
                onDragEnd={(draggedItem, monitor) => {
                    if (monitor.didDrop()) {
                        const dropResult = monitor.getDropResult();
                        if (dropResult && dropResult.index !== undefined) {
                            handleReorder(draggedItem, dropResult.index, flashcardsArray);
                        }
                    }
                }}
            >
                                                        <div className="resource-header">
                                                            <div className="resource-title">
                                                                <h5>{flashcard.title}</h5>
                                                                <p>{flashcard.description}</p>
                                                                <p>{flashcard.cards?.length || 0} cards</p>
                                                            </div>
                                                            <div className="resource-actions">
                        <div 
                            ref={drag} 
                            className="drag-handle"
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                        >
                            <MdDragIndicator />
                        </div>
                        <button 
                            onClick={handleMoveToTop}
                            className="move-btn move-top"
                            title="Move to Top"
                        >
                            ↑
                        </button>
                        <button 
                            onClick={handleMoveToBottom}
                            className="move-btn move-bottom"
                            title="Move to Bottom"
                        >
                            ↓
                        </button>
                                                                <Link
                                                                    to={getRoute(`courses/${courseId}/flashcard-sets/${flashcard.id}/items`)}
                                                                    className="manage-btn"
                                                                >
                                                                    Manage Cards
                                                                </Link>
                                                                <button
                                                                    className="edit-btn"
                                                                    onClick={() => {
                                                                        setEditFlashcardForm({
                                                                            id: flashcard.id,
                                                                            title: flashcard.title
                                                                        });
                                                                        setShowEditFlashcard(true);
                                                                    }}
                                                                >
                                                                    <MdEdit />
                                                                </button>
                                                                <button
                                                                    className="delete-btn"
                                                                    onClick={() => handleDeleteFlashcard(flashcard.id)}
                                                                >
                                                                    <MdDelete />
                                                                </button>
                                                            </div>
                                                        </div>
            </DraggableItem>
        );
    };

    if (isQbankLoading || isFileLoading || isFlashcardLoading) {
        return <div className="loading">Loading...</div>;
    }

    return (
        <div className="course-resources">

            <div className="sections-list">
                {activeData.sections?.map((section) => (
                    <div key={section.id} className="section-item">
                        <div className="section-header">
                            <div className="section-title" onClick={() => toggleSection(section.id)}>
                                {expandedSections[section.id] ? <MdExpandLess /> : <MdExpandMore />}
                                <h3>{section.name}</h3>
                                                    </div>
                        </div>

                        {expandedSections[section.id] && (
                            <div className="chapters-list">
                                {section.chapters?.map((chapter) => (
                                    <DropTarget
                                        key={chapter.id}
                                        acceptTypes={["qbank", "file", "flashcard"]}
                                        onDrop={async (item, monitor) => {
                                            if (item.type === 'qbank') {
                                                await handleTransfer(ENTITY_TYPES.QBANK, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                await refetchQbanks();
                                                await refetchAllData();
                                            } else if (item.type === 'file') {
                                                await handleTransfer(ENTITY_TYPES.FILE, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                await refetchFiles();
                                                await refetchAllData();
                                            } else if (item.type === 'flashcard') {
                                                await handleTransfer(ENTITY_TYPES.FLASHCARD, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                await refetchFlashcards();
                                                await refetchAllData();
                                            }
                                        }}
                                    >
                                        <div className="chapter-item">
                                        <div className="chapter-header">
                                            <div className="chapter-title" onClick={() => toggleChapter(chapter.id)}>
                                                {expandedChapters[chapter.id] ? <MdExpandLess /> : <MdExpandMore />}
                                                <h4>{chapter.title}</h4>
                                            </div>
                                            <div className="chapter-actions">
                                                {activeTab === 'qbanks' && (
                                                    <button onClick={() => {
                                                        setSelectedChapterId(chapter.id);
                                                        setShowAddQbank(true);
                                                    }}>
                                                        <MdAdd /> Add Qbank
                                                    </button>
                                                )}
                                                {activeTab === 'files' && (
                                                    <button onClick={() => {
                                                        setSelectedChapterId(chapter.id);
                                                        setShowAddFile(true);
                                                    }}>
                                                        <MdAdd /> Add File
                                                    </button>
                                                )}
                                                {activeTab === 'flashcards' && (
                                                    <button onClick={() => {
                                                        setSelectedChapterId(chapter.id);
                                                        setShowAddFlashcard(true);
                                                    }}>
                                                        <MdAdd /> Add Flashcard Set
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {expandedChapters[chapter.id] && (
                                            <div className="resources-list">
                                                    {activeTab === 'qbanks' && chapter.qbanks?.map((qbank, qbankIndex) => (
                                                    <DropTarget
                                                        key={qbank.id}
                                                        acceptTypes={["qbank", "file", "flashcard"]}
                                                        onDrop={async (item, monitor) => {
                                                            if (item.type === 'qbank' && item.id !== qbank.id) {
                                                                await qbankReorder.reorder(item, qbankIndex, SCOPE_TYPES.CHAPTER, chapter.id, chapter.qbanks);
                                                                await refetchQbanks();
                                                                await refetchAllData();
                                                            } else if (item.type === 'file') {
                                                                handleTransfer(ENTITY_TYPES.FILE, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                                await refetchAllData();
                                                            } else if (item.type === 'flashcard') {
                                                                handleTransfer(ENTITY_TYPES.FLASHCARD, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                                await refetchAllData();
                                                            }
                                                        }}
                                                    >
                                                        <QbankDragItem
                                                            qbank={qbank}
                                                            chapterId={chapter.id}
                                                            index={qbankIndex}
                                                            qbanksArray={chapter.qbanks}
                                                        />
                                                    </DropTarget>
                                                ))}

                                                {activeTab === 'files' && chapter.files?.map((file, fileIndex) => (
                                                    <DropTarget
                                                        key={file.id}
                                                        acceptTypes={["qbank", "file", "flashcard"]}
                                                        onDrop={async (item, monitor) => {
                                                            if (item.type === 'file' && item.id !== file.id) {
                                                                await fileReorder.reorder(item, fileIndex, SCOPE_TYPES.CHAPTER, chapter.id, chapter.files);
                                                                await refetchFiles();
                                                                await refetchAllData();
                                                            } else if (item.type === 'qbank') {
                                                                handleTransfer(ENTITY_TYPES.QBANK, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                                await refetchAllData();
                                                            } else if (item.type === 'flashcard') {
                                                                handleTransfer(ENTITY_TYPES.FLASHCARD, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                                await refetchAllData();
                                                            }
                                                        }}
                                                    >
                                                        <FileDragItem
                                                            file={file}
                                                            chapterId={chapter.id}
                                                            index={fileIndex}
                                                            filesArray={chapter.files}
                                                        />
                                                    </DropTarget>
                                                ))}

                                                {activeTab === 'flashcards' && chapter.flashcardSets?.map((flashcard, flashcardIndex) => (
                                                    <DropTarget
                                                        key={flashcard.id}
                                                        acceptTypes={["qbank", "file", "flashcard"]}
                                                        onDrop={async (item, monitor) => {
                                                            if (item.type === 'flashcard' && item.id !== flashcard.id) {
                                                                await flashcardReorder.reorder(item, flashcardIndex, SCOPE_TYPES.CHAPTER, chapter.id, chapter.flashcards);
                                                                await refetchFlashcards();
                                                                await refetchAllData();
                                                            } else if (item.type === 'qbank') {
                                                                handleTransfer(ENTITY_TYPES.QBANK, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                                await refetchAllData();
                                                            } else if (item.type === 'file') {
                                                                handleTransfer(ENTITY_TYPES.FILE, item.id, ENTITY_TYPES.CHAPTER, chapter.id);
                                                                await refetchAllData();
                                                            }
                                                        }}
                                                    >
                                                        <FlashcardDragItem
                                                            flashcard={flashcard}
                                                            chapterId={chapter.id}
                                                            index={flashcardIndex}
                                                            flashcardsArray={chapter.flashcards}
                                                        />
                                                    </DropTarget>
                                                ))}
                                            </div>
                                        )}
                                        </div>
                                    </DropTarget>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Add Qbank Modal */}
            {showAddQbank && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Add New Qbank</h3>
                        <form onSubmit={qbankFormik.handleSubmit}>
                            <input
                                type="text"
                                name="title"
                                placeholder="Qbank Title"
                                value={qbankFormik.values.title}
                                onChange={qbankFormik.handleChange}
                                onBlur={qbankFormik.handleBlur}
                            />
                            {qbankFormik.touched.title && qbankFormik.errors.title && (
                                <div className="error">{qbankFormik.errors.title}</div>
                            )}
                            <textarea
                                name="description"
                                placeholder="Qbank Description"
                                value={qbankFormik.values.description}
                                onChange={qbankFormik.handleChange}
                                onBlur={qbankFormik.handleBlur}
                            />
                            {qbankFormik.touched.description && qbankFormik.errors.description && (
                                <div className="error">{qbankFormik.errors.description}</div>
                            )}
                            <select
                                name="type"
                                value={qbankFormik.values.type}
                                onChange={qbankFormik.handleChange}
                                onBlur={qbankFormik.handleBlur}
                            >
                                <option value="Practice Quiz">Practice Quiz</option>
                                <option value="Graded Quiz">Graded Quiz</option>
                            </select>
                            {qbankFormik.touched.type && qbankFormik.errors.type && (
                                <div className="error">{qbankFormik.errors.type}</div>
                            )}
                            <div className="modal-actions">
                                <button type="submit">Add Qbank</button>
                                <button type="button" onClick={() => setShowAddQbank(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Qbank Modal */}
            {showEditQbank && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit Qbank</h3>
                        <form onSubmit={editQbankFormik.handleSubmit}>
                            <input
                                type="text"
                                name="title"
                                placeholder="Qbank Title"
                                value={editQbankFormik.values.title}
                                onChange={editQbankFormik.handleChange}
                                onBlur={editQbankFormik.handleBlur}
                            />
                            {editQbankFormik.touched.title && editQbankFormik.errors.title && (
                                <div className="error">{editQbankFormik.errors.title}</div>
                            )}
                            <textarea
                                name="description"
                                placeholder="Qbank Description"
                                value={editQbankFormik.values.description}
                                onChange={editQbankFormik.handleChange}
                                onBlur={editQbankFormik.handleBlur}
                            />
                            {editQbankFormik.touched.description && editQbankFormik.errors.description && (
                                <div className="error">{editQbankFormik.errors.description}</div>
                            )}
                            <select
                                name="type"
                                value={editQbankFormik.values.type}
                                onChange={editQbankFormik.handleChange}
                                onBlur={editQbankFormik.handleBlur}
                            >
                                <option value="Practice Quiz">Practice Quiz</option>
                                <option value="Graded Quiz">Graded Quiz</option>
                            </select>
                            {editQbankFormik.touched.type && editQbankFormik.errors.type && (
                                <div className="error">{editQbankFormik.errors.type}</div>
                            )}
                            <div className="modal-actions">
                                <button type="submit">Save Changes</button>
                                <button type="button" onClick={() => setShowEditQbank(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add File Modal */}
            {showAddFile && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Add New File</h3>
                        <form onSubmit={fileFormik.handleSubmit}>
                            <input
                                type="text"
                                name="title"
                                placeholder="File Title"
                                value={fileFormik.values.title}
                                onChange={fileFormik.handleChange}
                                onBlur={fileFormik.handleBlur}
                            />
                            {fileFormik.touched.title && fileFormik.errors.title && (
                                <div className="error">{fileFormik.errors.title}</div>
                            )}
                            <input
                                type="file"
                                name="file"
                                onChange={(e) => fileFormik.setFieldValue('file', e.target.files[0])}
                                onBlur={fileFormik.handleBlur}
                            />
                            {fileFormik.touched.file && fileFormik.errors.file && (
                                <div className="error">{fileFormik.errors.file}</div>
                            )}
                            <select
                                name="isDownload"
                                value={fileFormik.values.isDownload}
                                onChange={fileFormik.handleChange}
                                onBlur={fileFormik.handleBlur}
                            >
                                <option value={true}>Downloadable</option>
                                <option value={false}>Not Downloadable</option>
                            </select>
                            {fileFormik.touched.isDownload && fileFormik.errors.isDownload && (
                                <div className="error">{fileFormik.errors.isDownload}</div>
                            )}
                            <div className="modal-actions">
                                <button type="submit">Add File</button>
                                <button type="button" onClick={() => setShowAddFile(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit File Modal */}
            {showEditFile && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit File</h3>
                        <form onSubmit={editFileFormik.handleSubmit}>
                            <input
                                type="text"
                                name="title"
                                placeholder="File Title"
                                value={editFileFormik.values.title}
                                onChange={editFileFormik.handleChange}
                                onBlur={editFileFormik.handleBlur}
                            />
                            {editFileFormik.touched.title && editFileFormik.errors.title && (
                                <div className="error">{editFileFormik.errors.title}</div>
                            )}
                            <input
                                type="file"
                                name="file"
                                onChange={(e) => editFileFormik.setFieldValue('file', e.target.files[0])}
                            />
                            <small style={{ color: '#7f8c8d', fontSize: '0.75rem' }}>
                                Leave empty to keep the current file
                            </small>
                            <select
                                name="isDownload"
                                value={editFileFormik.values.isDownload}
                                onChange={editFileFormik.handleChange}
                                onBlur={editFileFormik.handleBlur}
                            >
                                <option value={true}>Downloadable</option>
                                <option value={false}>Not Downloadable</option>
                            </select>
                            {editFileFormik.touched.isDownload && editFileFormik.errors.isDownload && (
                                <div className="error">{editFileFormik.errors.isDownload}</div>
                            )}
                            <div className="modal-actions">
                                <button type="submit">Save Changes</button>
                                <button type="button" onClick={() => setShowEditFile(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Add Flashcard Modal */}
            {showAddFlashcard && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Add New Flashcard Set</h3>
                        <form onSubmit={flashcardFormik.handleSubmit}>
                            <input
                                type="text"
                                name="title"
                                placeholder="Flashcard Set Title"
                                value={flashcardFormik.values.title}
                                onChange={flashcardFormik.handleChange}
                                onBlur={flashcardFormik.handleBlur}
                            />
                            {flashcardFormik.touched.title && flashcardFormik.errors.title && (
                                <div className="error">{flashcardFormik.errors.title}</div>
                            )}
                            <div className="modal-actions">
                                <button type="submit">Add Flashcard Set</button>
                                <button type="button" onClick={() => setShowAddFlashcard(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Flashcard Modal */}
            {showEditFlashcard && (
                <div className="modal">
                    <div className="modal-content">
                        <h3>Edit Flashcard Set</h3>
                        <form onSubmit={editFlashcardFormik.handleSubmit}>
                            <input
                                type="text"
                                name="title"
                                placeholder="Flashcard Set Title"
                                value={editFlashcardFormik.values.title}
                                onChange={editFlashcardFormik.handleChange}
                                onBlur={editFlashcardFormik.handleBlur}
                            />
                            {editFlashcardFormik.touched.title && editFlashcardFormik.errors.title && (
                                <div className="error">{editFlashcardFormik.errors.title}</div>
                            )}
                            <div className="modal-actions">
                                <button type="submit">Save Changes</button>
                                <button type="button" onClick={() => setShowEditFlashcard(false)}>Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CourseResources; 